/** Task form for the "Labs" type. Adds lab type selector, urgent flag, and file attachments. */

import { useId } from 'react'
import { z } from 'zod'

import { LAB_TYPES, labTypeSchema } from '@snootsvet/shared'

import { useAppForm } from '@/frontend/components/form'
import { FieldWrapper } from '@/frontend/components/form/fields'
import { FileAttachments } from '@/frontend/components/form/file-attachments'
import { Button } from '@/frontend/components/ui/button'
import { Checkbox } from '@/frontend/components/ui/checkbox'
import { PatientSearch } from '@/frontend/features/patient/patient-search'
import { useFileAttachments } from '@/frontend/hooks/use-file-attachments'

import { useCreateTask } from '../use-create-task'
import {
  hasAssignee,
  hasClinicWhenRequired,
  TASK_ASSIGNMENT_DEFAULTS,
  TaskAssignmentFieldGroup,
  taskAssignmentShape,
} from './task-assignment.field-group'

const labsFormSchema = z
  .object({
    patientId: z
      .string()
      .min(1, 'Please select a patient')
      .nullable()
      .refine(Boolean, 'Please select a patient'),
    title: z.string().min(1, 'Title is required'),
    urgent: z.boolean(),
    // Lab type is optional — `''` represents "not specified".
    labType: z.union([labTypeSchema, z.literal('')]),
    ...taskAssignmentShape,
  })
  .refine(hasAssignee, {
    message: 'Please select a team or staff member',
    path: ['assigneeRole'],
  })
  .refine(hasClinicWhenRequired, {
    message: 'Please select a clinic',
    path: ['assigneeClinicId'],
  })

type LabsFormValues = z.input<typeof labsFormSchema>

export function LabsTaskForm(props: {
  defaultValues?: { patientId?: string }
  onSuccess?: () => void
  onCancel?: () => void
}) {
  const { mutateAsync: createTask } = useCreateTask()
  const { uploading, uploadPending, reset, fileCountError, attachmentProps } = useFileAttachments()
  const urgentId = useId()

  const form = useAppForm({
    defaultValues: {
      patientId: null,
      title: '',
      ...TASK_ASSIGNMENT_DEFAULTS,
      urgent: false,
      labType: '',
      ...props.defaultValues,
    } as LabsFormValues,
    validators: {
      onSubmit: labsFormSchema,
    },
    onSubmit: async ({ value, formApi }) => {
      try {
        if (fileCountError) {
          formApi.setErrorMap({ onSubmit: { form: fileCountError, fields: {} } })
          return
        }
        const fileKeys = await uploadPending()
        await createTask({
          title: value.title,
          patientId: value.patientId,
          data: { type: 'labs', urgent: value.urgent, labType: value.labType || undefined },
          assigneeRole: value.assigneeRole,
          assigneeClinicId: value.assigneeClinicId,
          assigneeStaffId: value.assigneeStaffId,
          ...(fileKeys.length > 0 ? { fileKeys } : {}),
        })
        form.reset()
        reset()
        props.onSuccess?.()
      } catch (err) {
        formApi.setErrorMap({
          onSubmit: {
            form: err instanceof Error ? err.message : 'Failed to create task',
            fields: {},
          },
        })
      }
    },
  })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      <form.AppForm>
        <div className="flex flex-col gap-4 py-2">
          <form.GlobalError />

          <form.AppField name="labType">
            {(field) => (
              <field.SelectField
                label="Lab type"
                items={[{ label: 'Not specified', value: '' }, ...LAB_TYPES]}
              />
            )}
          </form.AppField>

          {!props.defaultValues?.patientId && (
            <form.Field name="patientId">
              {(field) => (
                <FieldWrapper
                  field={field}
                  label="Patient"
                  required
                  render={({ id, disabled }) => (
                    <PatientSearch
                      id={id}
                      onChange={field.handleChange}
                      onBlur={field.handleBlur}
                      disabled={disabled}
                    />
                  )}
                />
              )}
            </form.Field>
          )}

          <form.AppField name="title">
            {(field) => (
              <field.InputField
                label="Reason / Clinical Question"
                placeholder="Brief summary"
                required
              />
            )}
          </form.AppField>

          <FileAttachments {...attachmentProps} />

          <form.Field name="urgent">
            {(field) => (
              <div className="flex items-center gap-2">
                <Checkbox
                  id={urgentId}
                  checked={field.state.value}
                  onCheckedChange={(checked) => field.handleChange(checked === true)}
                />
                <label htmlFor={urgentId} className="font-medium cursor-pointer">
                  Urgent
                </label>
              </div>
            )}
          </form.Field>

          <TaskAssignmentFieldGroup
            form={form}
            fields={{
              assigneeRole: 'assigneeRole',
              assigneeClinicId: 'assigneeClinicId',
              assigneeStaffId: 'assigneeStaffId',
            }}
          />
        </div>

        <form.Subscribe selector={(s) => [s.canSubmit, s.isSubmitting] as const}>
          {([canSubmit, isSubmitting]) => (
            <div className="flex items-center justify-end gap-2 pt-3">
              {props.onCancel && (
                <Button variant="outline" size="lg" type="button" onClick={props.onCancel}>
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                size="lg"
                disabled={!canSubmit || isSubmitting || uploading || !!fileCountError}
              >
                {isSubmitting ? 'Creating...' : 'Create Task'}
              </Button>
            </div>
          )}
        </form.Subscribe>
      </form.AppForm>
    </form>
  )
}
